import type { GetDashboardResponse, LayoutConfig, LayoutItem } from '@blockworks/platform/api/research/studio';

/**
 * Given a layout config and a list of visualizations,
 * find the top visualization in the layout and return
 * it's corresponding visualization data
 **/
const findTopVisualization = (
    layoutConfig: LayoutConfig,
    visualizations: GetDashboardResponse['data']['visualizations'],
) => {
    let topItem: LayoutItem | undefined;

    for (const item of layoutConfig.lg) {
        if (item.data.type === 'markdown') continue;

        if (
            !topItem ||
            item.y < topItem.y || // Prioritize lower y
            (item.y === topItem.y && item.x < topItem.x) // Break tie with x
        ) {
            topItem = item;
        }
    }

    const index = visualizations.findIndex(viz => viz.id === topItem?.data?.id);
    return { topMostVisualization: visualizations[index], index };
};

type FindTopVisualizationsDataArgs = {
    layoutConfig: LayoutConfig;
    visualizations: GetDashboardResponse['data']['visualizations'];
    isPublic: boolean;
};

type TopVisualization = NonNullable<GetDashboardResponse['data']['visualizations'][number]>;
type FindTopOrFeaturedVisualizationsOptions = {
    maxVisualizations?: number;
    maxVisualizationsForPublicDashboard?: number;
    maxFeaturedVisualizations?: number;
};

/**
 * Given a layout config and a list of visualizations, find the top `n` max amount
 * of visualizations or the top `n` max amount of featured visualizations (if they exist)
 * in the dashboard layout and return each visualization's data.
 *
 * Provide `max` options as a second argument to control the number of visualizations to return
 * in certain scenarios.
 **/
const findTopOrFeaturedVisualizations = (
    { layoutConfig, visualizations, isPublic }: FindTopVisualizationsDataArgs,
    {
        maxFeaturedVisualizations = 2,
        maxVisualizations = 1,
        maxVisualizationsForPublicDashboard = 2,
    }: FindTopOrFeaturedVisualizationsOptions = {},
): TopVisualization[] => {
    const layoutVisualizations = layoutConfig.lg.filter(item => item.data.type !== 'markdown');
    const featuredVisualizations = layoutVisualizations.filter(viz => viz.data.isFeatured);

    if (featuredVisualizations.length) {
        const topFeaturedVisualizations =
            featuredVisualizations
                .sort((a, b) => a.y - b.y || a.x - b.x) // Sort by y first, then x
                .slice(0, maxFeaturedVisualizations)
                .map(item => {
                    const index = visualizations.findIndex(viz => viz.id === item.data.id);
                    return visualizations[index];
                })
                .filter((item): item is TopVisualization => Boolean(item)) ?? [];

        return topFeaturedVisualizations;
    }

    const topVisualizations =
        layoutVisualizations
            .sort((a, b) => a.y - b.y || a.x - b.x) // Sort by y first, then x
            .slice(0, isPublic ? maxVisualizationsForPublicDashboard : maxVisualizations)
            .map(item => {
                const index = visualizations.findIndex(viz => viz.id === item.data.id);
                return visualizations[index];
            })
            .filter((item): item is TopVisualization => Boolean(item)) ?? [];

    return topVisualizations;
};

export { findTopOrFeaturedVisualizations, findTopVisualization };
